import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
	AlertTriangle,
	CalendarDays,
	Mail,
	MessageSquareText,
	Phone,
	UserPlus,
	type LucideIcon,
} from "lucide-react";

import type { PersonDetail } from "@/api/db/people";
import { Chip } from "@/components/people/field";
import { SourceBadges } from "@/components/people/source-badge";
import { format } from "@/lib/format";
import type { FichaFocus } from "@/lib/labels";
import { PessoaLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

function Photo({ url, name }: { url: string; name: string }) {
	const [failed, setFailed] = useState(false);

	if (failed) {
		return <Monogram name={name} />;
	}

	return (
		<img
			src={url}
			alt={name}
			onError={() => setFailed(true)}
			className="size-14 shrink-0 rounded-lg border border-border object-cover sm:size-16"
		/>
	);
}

function monogram(name: string) {
	const initials = name
		.split(/\s+/u)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]);

	return initials.join("").toUpperCase();
}

function Monogram({ name }: { name: string }) {
	return (
		<span className="grid size-14 shrink-0 place-items-center rounded-lg bg-primary-soft text-lg font-semibold text-primary sm:size-16 sm:text-xl">
			{monogram(name)}
		</span>
	);
}

function HeaderFact({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
	return (
		<span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
			<span className="text-muted-foreground">
				<Icon className="size-4" />
			</span>
			<span className="min-w-0 truncate">{children}</span>
		</span>
	);
}

function FactPill({
	id,
	focus,
	icon: Icon,
	label,
	tone,
}: {
	id: number;
	focus: FichaFocus;
	icon: LucideIcon;
	label: string;
	tone: "neutral" | "warn";
}) {
	return (
		<Link
			to="/pessoa/$id"
			params={{ id }}
			search={(prev) => ({ ...prev, tab: "cadastro", focus })}
			data-slot="fact-pill"
			data-focus={focus}
			className={cn(
				"inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
				tone === "warn"
					? "border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15"
					: "border-primary/20 bg-primary/10 text-primary hover:bg-primary/15",
			)}
		>
			<Icon className="size-3.5" />
			{label}
		</Link>
	);
}

function channelOnly(person: PersonDetail) {
	if (person.facet !== "lead") {
		return false;
	}

	if (person.cpf !== null) {
		return false;
	}

	return person.sendpulse_id !== null;
}

function HeaderPills({ person }: { person: PersonDetail }) {
	if (person.divergences.length === 0 && person.conflicts.length === 0 && !channelOnly(person)) {
		return null;
	}

	return (
		<div className="flex flex-wrap items-center gap-2">
			{channelOnly(person) && (
				<FactPill
					id={person.id}
					focus="cadastro"
					icon={UserPlus}
					tone="neutral"
					label={PessoaLabels.headerPill.completeRegistration}
				/>
			)}

			{person.divergences.length > 0 && (
				<FactPill
					id={person.id}
					focus="cadastro"
					icon={AlertTriangle}
					tone="warn"
					label={PessoaLabels.headerPill.divergences(person.divergences.length)}
				/>
			)}

			{person.conflicts.length > 0 && (
				<FactPill
					id={person.id}
					focus="cadastro"
					icon={AlertTriangle}
					tone="warn"
					label={PessoaLabels.headerPill.conflicts(person.conflicts.length)}
				/>
			)}
		</div>
	);
}

function HeaderContacts({ person }: { person: PersonDetail }) {
	const phone = person.phones.find((item) => item.primary)?.norm ?? person.phones[0]?.norm;
	const email = person.emails.find((item) => item.primary)?.email ?? person.emails[0]?.email;

	return (
		<div className="flex min-w-0 flex-wrap gap-x-5 gap-y-2">
			{!!phone && (
				<a
					href={phone.length <= 11 ? `tel:+55${phone}` : `tel:+${phone}`}
					className="rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				>
					<HeaderFact icon={Phone}>{format.phone(phone)}</HeaderFact>
				</a>
			)}

			{!!email && (
				<a
					href={`mailto:${email}`}
					className="rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				>
					<HeaderFact icon={Mail}>{email}</HeaderFact>
				</a>
			)}

			{person.age !== null && (
				<HeaderFact icon={CalendarDays}>{PessoaLabels.registry.years(person.age)}</HeaderFact>
			)}
		</div>
	);
}

export function FichaHeader({
	person,
	compact = false,
}: {
	person: PersonDetail;
	compact?: boolean;
}) {
	const sources = Array.from(new Set(person.sources.map((source) => source.source)));
	const displayName = format.readableName(person.name) ?? PessoaLabels.unnamedPatient;
	const facetLabel = person.facet === "lead" ? PessoaLabels.lead : PessoaLabels.patient;
	const facetTone = person.facet === "lead" ? "petrol" : "teal";

	if (compact) {
		const phone = person.phones.find((item) => item.primary)?.norm ?? person.phones[0]?.norm;
		return (
			<header className="flex min-w-0 flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-border p-4">
				<div className="min-w-0">
					<h1 className="break-words text-xl font-semibold tracking-tight sm:text-2xl">
						{displayName}
					</h1>
					<div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
						<span>{facetLabel}</span>
						{!!phone && <span>{format.phone(phone)}</span>}
					</div>
					{!!person.note && <p className="mt-2 text-xs text-muted-foreground">{person.note}</p>}
				</div>
				<HeaderPills person={person} />
			</header>
		);
	}

	return (
		<header className="overflow-hidden border-b border-border bg-card">
			<div className="grid min-w-0 grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
				<div className="flex min-w-0 items-center gap-4 p-4 sm:p-5">
					{!!person.photo_url && <Photo url={person.photo_url} name={displayName} />}
					{!person.photo_url && <Monogram name={displayName} />}

					<div className="min-w-0">
						<div className="mb-2 flex flex-wrap items-center gap-1.5">
							<Chip tone={facetTone}>{facetLabel}</Chip>
							<SourceBadges sources={sources} />

							{!!person.suspected_non_patient && (
								<Chip tone="coral">
									<AlertTriangle className="size-3" />
									{PessoaLabels.suspectedNonPatient}
								</Chip>
							)}
						</div>

						<h1 className="max-w-3xl break-words text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
							{displayName}
						</h1>

						{!!person.note && (
							<p className="mt-2 max-w-2xl text-sm italic text-muted-foreground">{person.note}</p>
						)}
					</div>
				</div>

				<aside className="flex min-w-0 flex-col justify-center gap-3 border-t border-border bg-background/50 p-4 sm:p-5 xl:border-l xl:border-t-0">
					<HeaderContacts person={person} />

					<HeaderPills person={person} />

					{person.sendpulse_id !== null && (
						<Link
							to="/pessoa/$id"
							params={{ id: person.id }}
							search={(prev) => ({ ...prev, tab: "conversa" })}
							className="control-button button-outline min-h-11 self-start px-3 py-2 md:min-h-9"
						>
							<MessageSquareText className="size-4" />
							{PessoaLabels.conversationLink}
						</Link>
					)}
				</aside>
			</div>
		</header>
	);
}
